import { useMemo, useState } from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { Add, ClockBack, Cross } from '@factorialco/f0-react/icons/app'
import {
  ContractVersionPageActivityPeriodFragment,
  ContractVersionPageContractVersionFragment,
  ContractVersionsPageEmployeeFragment,
} from 'generated/resources/graphql'
import moment from 'moment'

import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import PauseContractModal from 'modules/contracts/components/ContractDetails/ContractInformation/PauseContractModal'
import ResumeContractModal from 'modules/contracts/components/ContractDetails/ContractInformation/ResumeContractModal'
import { getContractStatus } from 'modules/contracts/components/ContractHistory/lib/getContractStatus'
import { getScheduleDescription } from 'modules/contracts/lib/scheduleDescription'
import { useRoute } from 'modules/contracts/pages/ContractVersionsPage/_RouteContext'

import Alert from './Alert'
import NewMenu from './NewMenu'
import { i18nDateToString, stringToDate } from 'modules/core/lib/dates'

const getSchedule = (
  selectedPeriod: ContractVersionPageActivityPeriodFragment,
  selectedContractVersion: ContractVersionPageContractVersionFragment
) => {
  const lastPeriod = selectedPeriod?.periods[0]
  if (
    lastPeriod?.schedule === 'upcoming' &&
    lastPeriod?.contractVersionId === selectedContractVersion.id
  ) {
    return 'upcoming'
  }
  return selectedPeriod?.schedule ?? 'ongoing'
}
interface Props {
  employee: ContractVersionsPageEmployeeFragment
  selectedContractVersion: ContractVersionPageContractVersionFragment
  contractVersions: ContractVersionPageContractVersionFragment[]
  hasContractVersionRequest: boolean
  onClickEdit: () => void
  setSelectedContractVersion: (contractVersionId: string | undefined) => void
  toggleHistory: () => void
  onClickFix: () => void
  onClickDelete: () => void
}

const NewHeader = ({
  employee,
  selectedContractVersion,
  contractVersions,
  onClickEdit,
  setSelectedContractVersion,
  toggleHistory,
  onClickFix,
  onClickDelete,
  hasContractVersionRequest,
}: Props) => {
  const route = useRoute()
  const canEdit = !!selectedContractVersion._policies?.edit?.can
  const canPromote = !!selectedContractVersion._policies?.promote?.can
  const canDeleteContracts = !!selectedContractVersion._policies?.delete?.can
  const [showPauseModal, setShowPauseModal] = useState(false)
  const [showResumeModal, setShowResumeModal] = useState(false)
  const canSeeMenu = canEdit || canDeleteContracts

  const contracts = useMemo(() => {
    return contractVersions.map((version) => version.contract)
  }, [contractVersions])
  const selectedContractVersionLastDay = useMemo(() => {
    const endDate = selectedContractVersion.endsOn
      ? stringToDate(selectedContractVersion.endsOn, 'backend')
      : undefined
    const futureEffectiveDates = contractVersions
      .filter((version) => version.effectiveOn > selectedContractVersion.effectiveOn)
      .map((version) => stringToDate(version.effectiveOn, 'backend'))

    // moment.min([]) returns now, we need to check if there is any future date before calling it
    const nextEffectiveDate =
      futureEffectiveDates.length > 0 ? moment.min(futureEffectiveDates) : undefined
    if (endDate && !nextEffectiveDate) return endDate
    if (nextEffectiveDate && !endDate) return nextEffectiveDate
    if (endDate && nextEffectiveDate) {
      return moment.min([endDate, nextEffectiveDate])
    }
    return undefined
  }, [selectedContractVersion.id, contractVersions])

  const globalOngoingActivityPeriod = contracts
    .find((contract) => contract?.schedule === 'ongoing')
    ?.activityPeriods?.find((period) => period.schedule === 'ongoing')

  const ongoingContract =
    contractVersions.find((version) =>
      globalOngoingActivityPeriod?.contractVersionIds.includes(version.id ?? '0')
    ) || selectedContractVersion

  const selectedContract = selectedContractVersion.contract
  const selectedPeriod = selectedContract?.activityPeriods.find(
    (p) => selectedContractVersion?.id && p.contractVersionIds.includes(selectedContractVersion.id)
  )
  const schedule = selectedPeriod ? getSchedule(selectedPeriod, selectedContractVersion) : 'ongoing'
  const description = getScheduleDescription(schedule, selectedContractVersion.effectiveOn)
  const { isOngoing, isUpcoming, isPassed } = useMemo(
    () => ({
      isOngoing: schedule === 'ongoing',
      isUpcoming: schedule === 'upcoming',
      isPassed: schedule === 'expired',
    }),
    [schedule]
  )
  const isPausable = selectedContract?.status?.isPausable
  const isResumable = selectedContract?.status?.isResumable
  const isFixedDiscontinuous = selectedContractVersion.contract?.isDiscontinuous
  const showActivityButton = canEdit && isFixedDiscontinuous && isOngoing
  const showBackToOngoingButton = (isPassed || isUpcoming) && !!globalOngoingActivityPeriod

  const activityCta = useMemo(() => {
    if (!showActivityButton) return null

    if (isResumable) {
      return {
        label: i18n.t('contract_versions.navigator.header.start_activity_period'),
        onClick: () => {
          track(trackingEvents.employee.contracts.startActivityPeriodStart)
          setShowResumeModal(true)
        },
      }
    }
    if (isPausable) {
      return {
        label: i18n.t('contract_versions.navigator.header.end_activity_period'),
        onClick: () => {
          track(trackingEvents.employee.contracts.endActivityPeriodStart)
          setShowPauseModal(true)
        },
      }
    }

    return null
  }, [showActivityButton, isResumable, isPausable])

  const onBackToOngoingContract = () => {
    window.history.replaceState(null, 'Factorial', route.contractVersionPath(ongoingContract.id))
    setSelectedContractVersion(ongoingContract.id ?? '0')
  }

  const canUpdateContract =
    (canEdit || canPromote) &&
    (isFixedDiscontinuous ? isOngoing && isPausable : isOngoing || isPassed)

  return (
    <>
      <Box flexDirection="column" gap="s24">
        <Box flexDirection="row" width="full" justifyContent="spaceBetween" alignItems="flexStart">
          <Box flexDirection="column" gap="s4">
            <Text.Normal size="300" weight="semibold" color="grey1000">
              {i18n.t('contract_versions.header.title', {
                name: employee.fullName ?? '',
                schedule: getContractStatus(schedule).toLocaleLowerCase(),
              })}
            </Text.Normal>
            <Text.Normal size="200" color="grey800">
              {description}
            </Text.Normal>
          </Box>
          <Box flexDirection="row" gap="s8">
            {activityCta && (
              <F0Button
                variant="default"
                onClick={activityCta.onClick}
                label={activityCta.label}
                size="sm"
              />
            )}
            {canUpdateContract && (
              <F0Button
                variant={activityCta ? 'outline' : 'default'}
                onClick={onClickEdit}
                icon={Add}
                label={i18n.t('contract_versions.header.new_conditions')}
              />
            )}
            {showBackToOngoingButton && (
              <F0Button
                variant="neutral"
                size="sm"
                onClick={onBackToOngoingContract}
                icon={Cross}
                label={i18n.t('contract_versions.navigator.history.back_to_current_conditions')}
              />
            )}
            <F0Button
              variant="outline"
              onClick={() => toggleHistory()}
              icon={ClockBack}
              hideLabel
              size="md"
            />
            {canSeeMenu && (
              <NewMenu
                employeeId={employee.id}
                selectedContractVersion={selectedContractVersion}
                allContractVersions={contractVersions}
                onClickFix={onClickFix}
                onClickDelete={onClickDelete}
                hasContractVersionRequest={hasContractVersionRequest}
              />
            )}
          </Box>
        </Box>
        {isPassed && (
          <Alert
            title={
              selectedContractVersionLastDay
                ? i18n.t('contract_versions.navigator.header.alerts.passed', {
                    date: i18nDateToString(
                      selectedContractVersionLastDay,
                      'ui_abbreviated_month_year'
                    ),
                  })
                : i18n.t('contract_versions.navigator.header.alerts.passed_unknown')
            }
            type="critical"
          />
        )}
        {selectedPeriod?.schedule === 'upcoming' ? (
          <Alert
            title={i18n.t('contract_versions.navigator.header.alerts.upcoming_conditions', {
              date: selectedContractVersion.effectiveOn
                ? moment(selectedContractVersion.effectiveOn).format('DD/MM/YYYY')
                : '',
            })}
            type="promote"
          />
        ) : (
          isUpcoming && (
            <Alert
              title={i18n.t('contract_versions.navigator.header.alerts.upcoming_activity', {
                date: selectedContractVersion.effectiveOn
                  ? moment(selectedContractVersion.effectiveOn).format('DD/MM/YYYY')
                  : '',
              })}
              type="promote"
            />
          )
        )}
      </Box>
      {showPauseModal && selectedContract?.id && selectedContractVersion.id && (
        <PauseContractModal
          contractId={selectedContract.id}
          employeeId={employee.id}
          lastEffectiveOnDate={new Date(selectedContractVersion.effectiveOn)}
          setSelectedContractVersion={setSelectedContractVersion}
          onClose={() => setShowPauseModal(false)}
        />
      )}
      {showResumeModal && selectedContract?.id && (
        <ResumeContractModal
          contractId={selectedContract.id}
          nextAvailableDate={selectedContractVersion.endsOn || selectedContractVersion.effectiveOn}
          onClose={() => setShowResumeModal(false)}
          setSelectedContractVersion={setSelectedContractVersion}
        />
      )}
    </>
  )
}

export default NewHeader
